const { getElement, getElements } = window.BAO.Utils

export class SiteDrawers extends window.BAO.CustomElement() {
  constructor () {
    super()

    if (this.hasAlreadyBeenUsed) {
      throw new Error(
        `You can only use 1 instance of ${this.nodeName.toLowerCase()}`,
      )
    }

    this._activeDrawerKey = ''
    this._transitionAbort = null
    this._closingDrawer = null
    this.drawers = []

    window.drawers = this

    this.drawerKeys
      .filter((key) => !this.triggerKeys.includes(key))
      .forEach((key) =>
        console.info(`Drawer exists for ${key} but there is no trigger`),
      )
  }

  setupListeners () {
    super.setupListeners()

    this.listeners.add(
      document.documentElement,
      'click',
      this.handleClick.bind(this),
    )

    this.listeners.add(
      document.documentElement,
      'keydown',
      this.handleKeydown.bind(this),
    )
  }

  /**
   * Closes whichever drawer is open when Escape is pressed. Always closes
   * fully (not just one level back for a nav sub-drawer), matching the
   * previous nav-specific behaviour this replaces.
   *
   * @param {KeyboardEvent} e
   *
   * @returns {void}
   */
  handleKeydown (e) {
    if (e.key !== 'Escape') return

    const openDrawer = this.drawers.find((drawer) => drawer.isOpen)
    if (!openDrawer) return

    const key = this.activeDrawerKey

    window.BAO.dispatchEvent(window.BAO.EVENTS.DRAWER.CLOSED, { key })

    this.activeDrawerKey = ''
  }

  handleClick (e) {
    const EVENTS = {
      TRIGGER: 'TRIGGER',
      BACK: 'BACK',
      CLOSE: 'CLOSE',
      BACKDROP: 'BACKDROP',
      UNKNOWN: 'UNKNOWN',
    }
    const eventType = (() => {
      if (e.target.closest(this.config.matches.trigger) !== null) {
        return EVENTS.TRIGGER
      } else if (e.target.closest(this.config.matches.back) !== null) {
        return EVENTS.BACK
      } else if (e.target.closest(this.config.matches.close) !== null) {
        return EVENTS.CLOSE
      } else if (e.target.closest(this.config.matches.backdrop) !== null) {
        return EVENTS.BACKDROP
      }

      return EVENTS.UNKNOWN
    })()

    if (eventType === EVENTS.UNKNOWN) return
    e.preventDefault()

    if (eventType === EVENTS.TRIGGER) {
      const trigger = e.target.closest(this.config.matches.trigger)
      const key = trigger.getAttribute('data-drawers-trigger')

      if (trigger.matches(this.config.matches.subNav)) {
        this.openSubDrawer(key)
        return
      }

      this.activeDrawerKey = key === this.activeDrawerKey ? '' : key

      window.BAO.dispatchEvent(window.BAO.EVENTS.DRAWER.OPENED, {
        key,
        trigger,
      })
    }

    if (eventType === EVENTS.BACK) {
      this.closeSubDrawer(e.target.closest(this.config.matches.back))
    }

    if (eventType === EVENTS.CLOSE || eventType === EVENTS.BACKDROP) {
      const key = this.activeDrawerKey

      window.BAO.dispatchEvent(window.BAO.EVENTS.DRAWER.CLOSED, {
        key,
      })

      this.activeDrawerKey = ''
    }
  }

  /**
   * Opens a drawer over the top of its parent instead of replacing it -
   * bypasses the activeDrawerKey setter's close-then-wait sequencing
   * entirely, since that's only meant for switching between unrelated
   * top-level drawers. The parent's own isOpen/active state is never
   * touched, so it stays visible behind the new drawer with no extra work.
   *
   * @param {string} key
   *
   * @returns {void}
   */
  openSubDrawer (key) {
    const drawer = this.drawers.find((d) => d.key === key)
    if (!drawer) return

    this._subDrawerReturnEl = document.activeElement

    this._activeDrawerKey = key
    drawer.isOpen = true

    window.BAO.Utils.trapFocus(drawer)
  }

  /**
   * Closes a drawer opened via openSubDrawer and returns to its parent,
   * restoring focus to whichever trigger opened it.
   *
   * @param {HTMLElement} backButton
   *
   * @returns {void}
   */
  closeSubDrawer (backButton) {
    const key = backButton.getAttribute('data-drawers-back')
    const parentKey = backButton.getAttribute('data-drawers-parent-key')

    const drawer = this.drawers.find((d) => d.key === key)
    if (drawer) drawer.isOpen = false

    this._activeDrawerKey = parentKey ?? ''

    const parentDrawer = this.drawers.find((d) => d.key === parentKey)
    if (parentDrawer) window.BAO.Utils.trapFocus(parentDrawer)

    this._subDrawerReturnEl?.focus()
    this._subDrawerReturnEl = null
  }

  /**
   * @param {string} key
   */
  open (key) {
    this.activeDrawerKey = key
  }

  close () {
    this.activeDrawerKey = ''
  }

  get triggerKeys () {
    return getElements('[data-drawers-trigger]').map((el) =>
      el.getAttribute('data-drawers-trigger'),
    )
  }

  get drawerKeys () {
    return getElements('[data-module-drawers-drawer]').map((el) =>
      el.getAttribute('data-module-drawers-drawer'),
    )
  }

  get hasAlreadyBeenUsed () {
    const el = getElement(this.nodeName.toLowerCase())

    return el !== this
  }

  get aDrawerIsOpen () {
    return this.drawers.some((drawer) => drawer.isOpen)
  }

  get activeDrawerKey () {
    return this._activeDrawerKey
  }

  set activeDrawerKey (key) {
    if (this._transitionAbort) {
      this._transitionAbort.abort()
      this._transitionAbort = null
    }

    const openDrawer = this.drawers.find((d) => d.isOpen)
    const drawerToWaitFor = openDrawer ?? this._closingDrawer

    if (key !== '' && drawerToWaitFor) {
      if (openDrawer) {
        this.activeDrawerKey = ''
      }

      const controller = new AbortController()
      this._transitionAbort = controller

      const onTransitionEnd = (e) => {
        if (e.propertyName !== 'transform') return
        drawerToWaitFor.removeEventListener('transitionend', onTransitionEnd)
        this._transitionAbort = null
        this.activeDrawerKey = key
      }

      drawerToWaitFor.addEventListener('transitionend', onTransitionEnd, {
        signal: controller.signal,
      })

      return
    }

    if (key === '') {
      const wasOpen = this.drawers.find((d) => d.isOpen)
      if (wasOpen) {
        this._closingDrawer = wasOpen
        const clearWhenDone = (e) => {
          if (e.propertyName !== 'transform') return
          wasOpen.removeEventListener('transitionend', clearWhenDone)
          if (this._closingDrawer === wasOpen) this._closingDrawer = null
        }
        wasOpen.addEventListener('transitionend', clearWhenDone)
      }
    } else {
      this._closingDrawer = null
    }

    if (key !== '') {
      this._focusReturnEl = document.activeElement
    }

    this._activeDrawerKey = key

    this.drawers.forEach((drawer) => (drawer.isOpen = drawer.key === key))

    this.handleFocusManagement(key)

    if (key === '') {
      this._focusReturnEl?.focus()
      this._focusReturnEl = null
    }

    this.config.elements.drawers.classList.toggle(
      this.config.classes.activeClass,
      this.aDrawerIsOpen,
    )
    document.body.classList.toggle(
      this.config.classes.siteOverflowed,
      this.aDrawerIsOpen,
    )
    document.body.classList.toggle(
      this.config.classes.drawersOpenUtility,
      this.aDrawerIsOpen,
    )

    if (key !== '') {
      this.els.drawer.element.classList.add('drw-Drawers_Drawer-active')
      document.body.classList.add(`drw-Drawers-${key}`)
    } else {
      this.els.drawer.element.classList.remove('drw-Drawers_Drawer-active')
    }

    setTimeout(() => {
      document.body.classList.toggle(
        this.config.classes.drawersOpenDelay,
        this.aDrawerIsOpen,
      )
    }, 4000)
  }

  /**
   * Traps focus inside the drawer while it's open. Scoped to
   * `.drw-Drawer-filters` and `.drw-Drawer-nav` only - every other drawer
   * still gets its focus returned on close (handled generically by the
   * activeDrawerKey setter), just without a hard trap while open.
   *
   * @param {string} key
   *
   * @returns {void}
   */
  handleFocusManagement (key) {
    if (key !== '') {
      const openedDrawer = this.drawers.find((drawer) => drawer.key === key)
      if (
        !openedDrawer?.classList.contains('drw-Drawer-filters') &&
        !openedDrawer?.classList.contains('drw-Drawer-nav')
      ) {
        return
      }

      window.BAO.Utils.trapFocus(openedDrawer)

      openedDrawer.querySelector(this.config.matches.focusable)?.focus()

      return
    }

    window.BAO.Utils.disableTrapFocus()
  }

  get config () {
    return {
      matches: {
        trigger: '[data-drawers-trigger]',
        subNav: '[data-drawers-sub-nav]',
        back: '[data-drawers-back]',
        close: '[data-drawers-close]',
        backdrop: '[data-site-drawers-el="backdrop"]',
        focusable:
          'button:not([disabled]), a[href]:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled])',
      },
      classes: {
        activeClass: 'drw-Drawers-active',
        activeDrawerClass: 'drw-Drawer-active',
        siteOverflowed: 'util-SiteOverflowed',
        drawersOpenUtility: 'util-DrawersOpen',
        drawersOpenDelay: 'util-DrawersOpenDelay',
      },
      elements: {
        body: document.body || document.documentElement,
        backdrop: '[data-site-drawers-el="backdrop"]',
        drawers: document.querySelector('site-drawers'),
      },
    }
  }
}

customElements.define('site-drawers', SiteDrawers)
